import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchClient } from "@/lib/api-client";

type Vote = "like" | "dislike";

interface VoteArgs {
  commentId: string;
  value: Vote;
  listKey: unknown[];
}

interface CachedItem {
  id: string;
  like_count: number;
  dislike_count: number;
  my_vote: Vote | null;
}

const applyOptimistic = (item: CachedItem, value: Vote): CachedItem => {
  const next = { ...item };
  if (item.my_vote === value) {
    next.my_vote = null;
    if (value === "like") next.like_count -= 1;
    else next.dislike_count -= 1;
  } else if (item.my_vote === null) {
    next.my_vote = value;
    if (value === "like") next.like_count += 1;
    else next.dislike_count += 1;
  } else {
    next.my_vote = value;
    if (value === "like") {
      next.like_count += 1;
      next.dislike_count -= 1;
    } else {
      next.dislike_count += 1;
      next.like_count -= 1;
    }
  }
  return next;
};

const patchPages = (
  data: { pages?: { data?: { result?: CachedItem[] } }[] } | undefined,
  commentId: string,
  fn: (i: CachedItem) => CachedItem,
) => {
  if (!data?.pages) return data;
  return {
    ...data,
    pages: data.pages.map((pg) => ({
      ...pg,
      data: {
        ...pg.data,
        result: (pg.data?.result ?? []).map((i) =>
          i.id === commentId ? fn(i) : i,
        ),
      },
    })),
  };
};

export const useVoteBlogComment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ commentId, value }: VoteArgs) => {
      const response = await fetchClient.POST("/blog/comments/{id}/vote", {
        params: { path: { id: commentId } },
        body: { value },
      });
      const err = (response as { error?: unknown }).error;
      if (err) throw err;
      return response.data;
    },
    onMutate: async ({ commentId, value, listKey }) => {
      await qc.cancelQueries({ queryKey: listKey });
      const prev = qc.getQueryData(listKey);
      qc.setQueryData(listKey, (d: never) =>
        patchPages(d, commentId, (i) => applyOptimistic(i, value)),
      );
      return { prev, listKey };
    },
    onError: (_e, _vars, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(ctx.listKey, ctx.prev);
    },
    onSuccess: (data, { commentId, listKey }) => {
      const auth = (data as { data?: CachedItem } | undefined)?.data;
      if (!auth) return;
      qc.setQueryData(listKey, (d: never) =>
        patchPages(d, commentId, (i) => ({
          ...i,
          like_count: auth.like_count,
          dislike_count: auth.dislike_count,
          my_vote: auth.my_vote,
        })),
      );
    },
  });
};
